import { Component, ReactNode } from "react"

import ErrorWithData from "@/utils/ErrorWithData"
import { captureException } from "@/lib/Tracing"

// import ToastContext from "@/context/ToastContext"

const IS_LOCAL = import.meta.env.NODE_ENV === "development"

const IGNORED_ERRORS: string[] = []

/**
 * ErrorBoundary component for catching JavaScript errors anywhere in the child component tree.
 * Displays a fallback UI and optionally reports errors.
 * @note Error boundaries currently have to be a class component.
 *
 * @class
 * @extends React.Component
 * @param {Object} props - The props for the ErrorBoundary component.
 * @param {ReactNode} props.children - The child components.
 * @param {ReactNode} [props.fallback] - Optional fallback UI to display on error.
 * @param {boolean} [props.report] - Whether to report errors (default: true in dev).
 * @param {(error?: any) => void} [props.onCatch] - Optional callback when an error is caught.
 */
class ErrorBoundary extends Component<
  { children: ReactNode; fallback?: ReactNode; report?: boolean; onCatch?: (error?: any) => void },
  any
> {
  // static contextType = ToastContext

  // declare context: ContextType<typeof ToastContext>

  constructor(props: any) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: any) {
    if (IGNORED_ERRORS.includes(error.message)) {
      return {}
    }
    return {
      hasError: true,
      error,
    }
  }

  static shouldComponentUpdate(_nextProps: any, nextState: any) {
    return !nextState.hasError
  }

  componentDidUpdate(_prevProps: any, prevState: { hasError: boolean; error: typeof ErrorWithData }) {
    const { hasError, error } = this.state
    const { report = IS_LOCAL, onCatch } = this.props
    // const { addToast } = this.context
    if (hasError && !prevState.hasError && !!onCatch) {
      onCatch(error)
    }
    if (hasError && !prevState.hasError) {
      captureException(error)
    }
    if (hasError && !prevState.hasError && report) {
      // addToast(error, "error")
    }
  }

  render() {
    const { hasError } = this.state
    const { report = IS_LOCAL, children, fallback } = this.props

    if (hasError && fallback) {
      return fallback
    }

    if (hasError && report) {
      return (
        <button
          className="bg-brown-600 rounded-2xl px-3 py-5 text-black"
          type="button"
          onClick={() => {
            this.setState({ hasError: false, error: null })
          }}
        >
          Error → Refresh Boundary
        </button>
      )
    }

    if (hasError) {
      return null
    }
    return children
  }
}

export default ErrorBoundary
